import { Box, Tabs, Tab } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import type { ReactNode } from "react";
import CreatorPortal from "../../components/CreatorPortal/CreatorPortal";
import GraphExplorer from "../graph/GraphExplorer";
import PromptComponentManager from "../../components/PromptComponent/PromptComponentManager";
import FeedbackTab from "./FeedbackTab";
import GoalLibraryPage from "./GoalLibraryPage";
import MethodologyPresetPage from "./MethodologyPresetPage";
import SynapseIntrospectionTab from "./SynapseIntrospectionTab";
import IngestionTab from "./IngestionTab";
import ConversationResultsPage from "../conversation-results/ConversationResultsPage";
import TranscriptsTab from "./TranscriptsTab";
import ErrorsTab from "./ErrorsTab";
import { useLocation, useNavigate } from "react-router-dom";
import { useUserInfoContext } from "../../lib/contexts/UserInfoContext";
import { isSuperAdmin } from "@common/domain/auth";
import { useEffect, useRef } from "react";
import {
  findTabByPath,
  getVisibleGroups,
  getVisibleTabs,
  type AdminTabKey,
} from "./adminTabs";

const tabStyles = {
  minHeight: 48,
  "& .MuiTabs-scroller": { overflowX: "auto !important" },
  "& .MuiTab-root": {
    flexShrink: 0,
    minWidth: "max-content",
    px: { xs: 1.5, sm: 2 },
  },
};

function TabPanel({ children }: { children: ReactNode }) {
  return (
    <Box
      role="tabpanel"
      sx={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}>{children}</Box>
    </Box>
  );
}

const AdminPage = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { userInfo } = useUserInfoContext();

  const isFullAdmin = isSuperAdmin(userInfo?.groups);
  const tabs = getVisibleTabs(isFullAdmin);
  const groups = getVisibleGroups(tabs);

  const matchedTab = findTabByPath(tabs, pathname);
  // Unknown or hidden paths fall back to the first visible tab.
  const activeTab = matchedTab ?? tabs[0];
  const groupTabs = tabs.filter((t) => t.group === activeTab.group);

  // tabs is already filtered for this user, so no match means an unknown or forbidden path.
  useEffect(() => {
    if (!matchedTab) navigate(activeTab.path, { replace: true });
  }, [matchedTab, activeTab.path, navigate]);

  // Clicking a group returns to the last tab visited in it, so heavy tabs (e.g. Graph Explorer) don't mount needlessly.
  const lastPathByGroup = useRef<Record<string, string>>({});
  lastPathByGroup.current[activeTab.group] = activeTab.path;

  const isSynapseDemoRoute = pathname === "/admin/synapse/demo";

  const panels: Record<AdminTabKey, ReactNode> = {
    personas: <CreatorPortal />,
    prompts: <PromptComponentManager />,
    ingestions: <IngestionTab />,
    goals: <GoalLibraryPage />,
    methodology: <MethodologyPresetPage />,
    "conversation-results": <ConversationResultsPage />,
    feedback: <FeedbackTab />,
    graph: <GraphExplorer />,
    synapse: <SynapseIntrospectionTab forceClientDemoMode={isSynapseDemoRoute} clientDemoMode={isSynapseDemoRoute} />,
    transcripts: <TranscriptsTab />,
    errors: <ErrorsTab />,
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "calc(100dvh - 48px)",
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <Box sx={{ borderBottom: 1, borderColor: "divider", px: { xs: 1, sm: 2 }, minWidth: 0 }}>
        <Tabs
          aria-label="Admin sections"
          value={activeTab.group}
          onChange={(_, group) =>
            navigate(lastPathByGroup.current[group] ?? tabs.find((t) => t.group === group)!.path)
          }
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={tabStyles}
        >
          {groups.map((g) => (
            <Tab key={g.id} value={g.id} label={g.label} sx={{ fontWeight: 700, fontSize: "1rem", minHeight: 44 }} />
          ))}
        </Tabs>
        <Tabs
          aria-label={`${groups.find((g) => g.id === activeTab.group)?.label} tabs`}
          value={activeTab.key}
          onChange={(_, key) => navigate(tabs.find((t) => t.key === key)!.path)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            ...tabStyles,
            minHeight: 36,
            py: 0.5,
            "& .MuiTabs-indicator": { display: "none" },
            "& .MuiTabs-flexContainer": { gap: 0.5 },
            "& .MuiTab-root": {
              ...tabStyles["& .MuiTab-root"],
              minHeight: 28,
              py: 0.25,
              fontSize: "0.85rem",
              borderRadius: 999,
              color: "text.secondary",
            },
            "& .MuiTab-root.Mui-selected": {
              color: "text.primary",
              fontWeight: 600,
              bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.16),
            },
          }}
        >
          {groupTabs.map((t) => (
            <Tab key={t.key} value={t.key} label={t.label} />
          ))}
        </Tabs>
      </Box>
      <TabPanel>{panels[activeTab.key]}</TabPanel>
    </Box>
  );
};

export default AdminPage;
